// Tiny DOM helpers — the app is framework-free to keep the bundle small.

type Attrs = Record<string, unknown>;
type Child = Node | string | null | undefined | false;

/** Create an element: el("div.card#id", { onclick }, ...children). */
export function el(spec: string, attrs: Attrs = {}, ...children: Child[]): HTMLElement {
  const [tagAndId, ...classes] = spec.split(".");
  const [tag, id] = tagAndId.split("#");
  const node = document.createElement(tag || "div");
  if (id) node.id = id;
  if (classes.length) node.className = classes.join(" ");
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === "class") node.className = String(v);
    else if (k === "html") node.innerHTML = String(v);
    else if (k.startsWith("on") && typeof v === "function") {
      node.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    } else if (k === "dataset" && typeof v === "object") {
      Object.assign(node.dataset, v);
    } else {
      node.setAttribute(k, String(v));
    }
  }
  for (const c of children) {
    if (c == null || c === false) continue;
    node.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
  return node;
}

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}

export function mount(node: HTMLElement): void {
  const root = document.querySelector<HTMLDivElement>("#app")!;
  root.replaceChildren(node);
}

/** Terminal-style typing effect for CONTROL's messages. Returns when done. */
export function typeInto(
  target: HTMLElement,
  text: string,
  speed = 18,
): Promise<void> {
  return new Promise((resolve) => {
    target.textContent = "";
    let i = 0;
    const cursor = el("span.cursor", {}, "▋");
    target.append(cursor);
    const tick = () => {
      if (i >= text.length) {
        cursor.remove();
        resolve();
        return;
      }
      cursor.before(document.createTextNode(text[i++]));
      setTimeout(tick, text[i - 1] === "\n" ? speed * 6 : speed);
    };
    tick();
  });
}

/** Lightweight toast in CONTROL's voice. */
export function toast(msg: string, kind: "ok" | "warn" | "err" = "ok"): void {
  const t = el(`div.toast.toast--${kind}`, {}, msg);
  document.body.append(t);
  requestAnimationFrame(() => t.classList.add("toast--in"));
  setTimeout(() => {
    t.classList.remove("toast--in");
    setTimeout(() => t.remove(), 300);
  }, 3200);
}
